import React, { createContext, useContext, useEffect, useState, useCallback, useRef } from 'react';
import client, { setToken } from '@/lib/api';
import { toast } from 'sonner';

const AuthContext = createContext(null);
export const useAuth = () => useContext(AuthContext);

// Auto-logout thresholds (Directive 1)
const INACTIVITY_MS = 15 * 60 * 1000; // 15 minutes of no interaction
const HIDDEN_GRACE_MS = 60 * 1000;    // 60s after tab/app backgrounded (screen off)
const REMEMBER_KEY = 'hibret_email';

export const getRememberedEmail = () => localStorage.getItem(REMEMBER_KEY) || '';

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [tenant, setTenant] = useState(null);
  const [tenantLoading, setTenantLoading] = useState(true);
  const manual = useRef(false);
  const userRef = useRef(null);
  const inactivityTimer = useRef(null);
  const hiddenTimer = useRef(null);
  const warnTimer = useRef(null);
  const lastActivity = useRef(Date.now());

  useEffect(() => { userRef.current = user; }, [user]);

  const refreshTenant = useCallback(async () => {
    try {
      const res = await client.get('/tenant/me');
      setTenant(res.data);
      return res.data;
    } catch (e) {
      setTenant(null);
      return null;
    } finally {
      setTenantLoading(false);
    }
  }, []);

  const checkAuth = useCallback(async () => {
    try {
      const res = await client.get('/auth/me');
      setUser(res.data);
      await refreshTenant();
    } catch (e) {
      if (!manual.current) setUser(null);
      setTenant(null);
      setTenantLoading(false);
    } finally {
      setLoading(false);
    }
  }, [refreshTenant]);

  useEffect(() => {
    const h = window.location.hash || '';
    if (h.includes('session_id=') || h.includes('gtoken=') || h.includes('gpend=') || h.includes('gerror=')) {
      setLoading(false);
      return;
    }
    checkAuth();
  }, [checkAuth]);

  const rememberEmail = (email) => { if (email) localStorage.setItem(REMEMBER_KEY, email); };

  const login = async (email, password) => {
    const res = await client.post('/auth/login', { email, password });
    manual.current = true;
    setToken(res.data.token);
    setUser(res.data.user);
    rememberEmail(res.data.user?.email || email);
    setTenantLoading(true);
    await refreshTenant();
    return res.data.user;
  };

  const finishAuth = async (data) => {
    manual.current = true;
    setToken(data.token);
    setUser(data.user);
    rememberEmail(data.user?.email);
    setTenantLoading(true);
    await refreshTenant();
    return data.user;
  };

  const doLogout = useCallback((reason) => {
    client.post('/auth/logout').catch(() => {});
    manual.current = false;
    setToken(null);
    setUser(null);
    setTenant(null);
    if (reason) toast.info(reason);
  }, []);

  const logout = useCallback(async () => { doLogout(); }, [doLogout]);

  // ---- Auto-logout: inactivity + background/screen-off ----
  const resetInactivity = useCallback(() => {
    lastActivity.current = Date.now();
    if (inactivityTimer.current) clearTimeout(inactivityTimer.current);
    if (warnTimer.current) clearTimeout(warnTimer.current);
    warnTimer.current = setTimeout(() => {
      if (userRef.current) toast.warning("You'll be signed out in 1 minute due to inactivity. Move your mouse or tap to stay signed in.");
    }, Math.max(0, INACTIVITY_MS - 60 * 1000));
    inactivityTimer.current = setTimeout(() => {
      if (userRef.current) doLogout('You were signed out due to inactivity. Please sign in again.');
    }, INACTIVITY_MS);
  }, [doLogout]);

  const getSessionRemainingMs = useCallback(() => Math.max(0, INACTIVITY_MS - (Date.now() - lastActivity.current)), []);

  useEffect(() => {
    if (!user) {
      if (inactivityTimer.current) clearTimeout(inactivityTimer.current);
      if (hiddenTimer.current) clearTimeout(hiddenTimer.current);
      return;
    }
    const activityEvents = ['mousemove', 'mousedown', 'keydown', 'touchstart', 'scroll', 'click'];
    const onActivity = () => resetInactivity();
    activityEvents.forEach((e) => window.addEventListener(e, onActivity, { passive: true }));
    resetInactivity();

    const onVisibility = () => {
      if (document.hidden) {
        if (hiddenTimer.current) clearTimeout(hiddenTimer.current);
        hiddenTimer.current = setTimeout(() => {
          if (userRef.current) doLogout('You were signed out because the app was inactive/closed. Please sign in again.');
        }, HIDDEN_GRACE_MS);
      } else if (hiddenTimer.current) {
        clearTimeout(hiddenTimer.current);
        hiddenTimer.current = null;
      }
    };
    document.addEventListener('visibilitychange', onVisibility);

    return () => {
      activityEvents.forEach((e) => window.removeEventListener(e, onActivity));
      document.removeEventListener('visibilitychange', onVisibility);
      if (inactivityTimer.current) clearTimeout(inactivityTimer.current);
      if (hiddenTimer.current) clearTimeout(hiddenTimer.current);
      if (warnTimer.current) clearTimeout(warnTimer.current);
    };
  }, [user, resetInactivity, doLogout]);

  return (
    <AuthContext.Provider value={{ user, setUser, loading, tenant, tenantLoading, refreshTenant, login, finishAuth, logout, checkAuth, rememberEmail, getSessionRemainingMs, idleLimitMs: INACTIVITY_MS }}>
      {children}
    </AuthContext.Provider>
  );
}
